import BrandLiveRateCardForm from '../../components/brand-live/brand-live-rate-card-form';
import BrandLiveSlots from '../../components/brand-live/brand-live-slots';
import BrandLiveCreatorTab from '../../components/brand-live/brand-live-creator-tab';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Briefcase,
  ClipboardList,
  FileText,
  Handshake,
  Inbox,
  LayoutGrid,
  Mail,
  MousePointerClick,
  Radio,
  Send,
  Settings,
  Star,
} from 'lucide-react';
import {
  GroupedNavGroup,
  GroupedSectionNavDesktop,
  GroupedSectionNavMobile,
} from '../../components/section-nav/grouped-section-nav';
import { RootState } from '../../configuration';
import {
  acceptBriefInvite,
  acceptBrandDeal,
  BrandCreatorSetting,
  CreatorDeal,
  declineBrandDeal,
  getBrandCreatorSetting,
  getCreatorPendingRatings,
  getMyBrandDeals,
  getMyBrandTasks,
  requestBrandDealEdit,
  submitCreatorRating,
  setBrandCreatorSetting,
} from '../../api/payment/brand-deal.api';
import { listCreatorBrandBookings } from '../../api/payment/brand-live.api';
import { dealStatusLabel } from '../../utils/brand-deal';
import { formatRate } from '../../utils/brand-conversion';
import { resolveActiveTab } from '../../utils/section-tabs';
import BrandMyProposals from '../../components/brand-deal/brand-my-proposals';
import BrandTaskList from '../../components/brand-deal/brand-task-list';
import BrandMarketApplications, {
  BrandMarketApplicationsHandle,
} from '../../components/brand-deal/brand-market-applications';
import BrandRatingPendingList, {
  BrandRatingPendingListHandle,
  showNeedToRateToast,
} from '../../components/brand-deal/brand-rating-pending';
import { BrandRatingBox } from '../../components/brand-deal/brand-rating-form';
import IndividualOrdersToggle from '../../components/brand-deal/individual-orders-toggle';
import CreatorTaxCodeField from '../../components/brand-deal/creator-tax-code-field';
import { useCreatorBrandProfile } from '../../hooks/use-creator-brand-profile';
import { useBrandTaxConsent } from '../../hooks/use-brand-tax-consent';
import BrandInviteCard from '../../components/brand-deal/brand-invite-card';
import { acceptConfirmText, isContentConflict } from '../../utils/brand-deal-content';
import { BrandThreadLink } from '../../components/brand-deal/brand-thread-panel';
import BrandBriefInviteCard from '../../components/brand-deal/brand-brief-invite-card';
import { acceptBriefConfirmText } from '../../utils/brand-brief-invite';
import { routePaths } from '../../routes/routes-path';
import { taskTurn } from '../../utils/brand-turn';

/**
 * Trang "Hợp tác quảng cáo" của creator: bật/tắt nhận deal, hộp lời mời đang chờ (xem trước bài +
 * Từ chối / Đồng ý & Đăng ngay), deal đang chạy + lịch sử. Mọi hành động chặn bấm hai lần (khoá
 * theo id) — Đồng ý hai lần/hai tab chỉ 1 lần đăng bài thật sự (payment đã xử lý concurrency).
 */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const formatVnd = (value: number) => `${Number(value || 0).toLocaleString('vi-VN')}đ`;

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const RUNNING_STATUSES = ['Publishing', 'Live'];

const BrandDealsPage: React.FC = () => {
  const profile = useSelector((state: RootState) => state.features.auth.profile);
  const collab = useCreatorBrandProfile(profile?.userId);

  const [loading, setLoading] = useState(true);
  const [setting, setSetting] = useState<BrandCreatorSetting | null>(null);
  const [settingError, setSettingError] = useState<string | null>(null);
  const [deals, setDeals] = useState<CreatorDeal[]>([]);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const inFlightRef = useRef<Set<string>>(new Set());
  const taxConsent = useBrandTaxConsent();
  const navigate = useNavigate();

  // Giữ nguyên các khoá cũ (invites/live/ratings/tasks...) vì thông báo đang link tới `?tab=...`; chỉ thêm
  // 'deals' (deal đang chạy + lịch sử, trước nằm chung tab lời mời) và 'live-orders' (đơn livestream chờ nhận,
  // trước nằm chung tab Livestream) để tách rõ yêu cầu nhận được với việc đang làm.
  type Tab = 'invites' | 'market' | 'applications' | 'proposals' | 'briefs' | 'tasks' | 'ratings' | 'live' | 'live-orders' | 'deals' | 'settings';
  const TAB_KEYS: Tab[] = ['invites', 'market', 'applications', 'proposals', 'briefs', 'tasks', 'ratings', 'live', 'live-orders', 'deals', 'settings'];
  const [searchParams, setSearchParams] = useSearchParams();
  // Thông báo "Nộp bài"/"Yêu cầu sửa"/"Từ chối" link tới `?tab=tasks` (xem M3 §2.4), thông báo chấm
  // điểm (M5 §2.4) link tới `?tab=ratings` ⇒ mở thẳng đúng tab; không có/sai ?tab ⇒ về "Lời mời trực
  // tiếp" như trước.
  const tab = (resolveActiveTab(TAB_KEYS, searchParams.get('tab'), 'invites') || 'invites') as Tab;
  const setTab = (key: Tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };
  // Link cũ ?vai=thue (từng gửi trong vài thông báo và trang Quảng cáo) nay chuyển sang trang Thuê creator riêng.
  const legacyOrdererLink = searchParams.get('vai') === 'thue';
  const applicationsRef = useRef<BrandMarketApplicationsHandle>(null);
  const ratingsRef = useRef<BrandRatingPendingListHandle>(null);
  const [slotsReloadKey, setSlotsReloadKey] = useState(0);
  const [pendingRatingsCount, setPendingRatingsCount] = useState(0);
  const [pendingLiveOrders, setPendingLiveOrders] = useState(0);
  // Bài brief / đề xuất đang chờ chính creator làm (soạn, sửa, bấm đăng) — số trên dải "Việc cần bạn làm".
  const [myTurnTasks, setMyTurnTasks] = useState(0);
  const goToRatingsTab = () => setTab('ratings');

  const load = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([getBrandCreatorSetting(), getMyBrandDeals()]);
      setSetting(s);
      setDeals(d);
    } catch (err) {
      toast.error(errorMessage(err, 'Không tải được Sàn hợp tác sáng tạo.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // M5: dải nhắc "Bạn có N deal cần chấm điểm" hiện ở MỌI tab (không chỉ tab "Chờ chấm điểm") — tải
  // riêng ngay từ đầu, không chờ người dùng mở tab đó. `BrandRatingPendingList` (tab "Chờ chấm điểm")
  // tự đồng bộ lại số này qua `onCountChange` khi mở tab/chấm xong 1 deal.
  useEffect(() => {
    getCreatorPendingRatings()
      .then((res) => setPendingRatingsCount(res.length))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    getMyBrandTasks()
      .then((list) => setMyTurnTasks(list.filter((t) => taskTurn(t.status) === 'mine').length))
      .catch(() => undefined);
  }, [tab]);

  // Số đơn livestream chờ nhận cho huy hiệu trên mục lục; tải lại khi tab Livestream báo có thay đổi.
  useEffect(() => {
    listCreatorBrandBookings()
      .then((list) =>
        setPendingLiveOrders(list.filter((b) => b.live?.status === 'Invited' || b.post?.status === 'Invited').length)
      )
      .catch(() => undefined);
  }, [slotsReloadKey]);

  // Chặn bấm hai lần (double-submit lock): mỗi key (toggle / accept:id / decline:id) chỉ 1 request
  // đang bay cùng lúc.
  const run = async <T,>(
    key: string,
    fn: () => Promise<T>,
    onOk: (result: T) => void,
    fallback: string,
    onGoToRatingsTab?: () => void,
    onTaxConsent?: () => void
  ) => {
    if (inFlightRef.current.has(key)) return;
    inFlightRef.current.add(key);
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      onOk(await fn());
    } catch (err) {
      // Chưa đồng ý điều khoản thuế: mở hộp điều khoản thay cho thông báo lỗi; đồng ý xong tự gọi lại onTaxConsent.
      if (!(onTaxConsent && taxConsent.handle(err, onTaxConsent))) showNeedToRateToast(errorMessage(err, fallback), onGoToRatingsTab);
      throw err;
    } finally {
      inFlightRef.current.delete(key);
      setBusy((b) => {
        const next = { ...b };
        delete next[key];
        return next;
      });
    }
  };

  const handleToggle = () => {
    const next = !setting?.acceptDeals;
    run(
      'toggle',
      () => setBrandCreatorSetting(next),
      (res) => {
        setSetting(res);
        setSettingError(null);
        if (next) toast.success('Đã bật nhận deal quảng cáo.');
      },
      'Không đổi được cài đặt, thử lại sau.'
    ).catch((err) => {
      if (next) setSettingError(errorMessage(err, 'Không bật được nhận deal quảng cáo.'));
    });
  };

  const handleDecline = (deal: CreatorDeal) => {
    if (!window.confirm('Từ chối lời mời này? Doanh nghiệp sẽ được hoàn lại tiền đã ký quỹ.')) return;
    run(
      `decline:${deal.id}`,
      () => declineBrandDeal(deal.id),
      () => {
        toast.success('Đã từ chối lời mời.');
        // Tải lại từ máy chủ thay vì chỉ vá state cục bộ — không bỏ sót thay đổi khác (vd
        // validClicks, một tab khác vừa thao tác) ngoài 2 field vừa đổi.
        load();
      },
      'Không từ chối được lời mời này.'
    ).catch(() => undefined);
  };

  // Gọi lại sau khi đồng ý điều khoản thuế thì không hỏi xác nhận lần nữa (creator đã xác nhận trước đó).
  // Gửi kèm số bản nội dung đang xem; máy chủ trả 409 nếu người đặt vừa sửa ⇒ toast câu máy chủ (run đã hiện) rồi tải bản mới.
  const doAccept = (deal: CreatorDeal) => {
    run(
      `accept:${deal.id}`,
      () => acceptBrandDeal(deal.id, deal.contentVersion),
      (res) => {
        if (res.message) {
          // Chưa Live thật: đăng lỗi (job nền thử lại) hoặc bài có sẵn không còn dùng được (đơn quay về chờ trả lời).
          toast(res.message, { icon: 'ℹ️' });
        } else {
          toast.success(deal.existingPostId ? 'Đã gắn nhãn hợp tác vào bài có sẵn.' : 'Đã đăng bài hợp tác quảng cáo.');
        }
        load();
      },
      'Không nhận được deal này.',
      goToRatingsTab,
      () => doAccept(deal)
    ).catch((err) => {
      if (isContentConflict(err)) load();
    });
  };

  const handleAccept = (deal: CreatorDeal) => {
    if (!window.confirm(acceptConfirmText(deal))) return;
    doAccept(deal);
  };

  // Lời mời làm brief: gửi kèm bản brief đang xem; máy chủ trả 409 nếu người đặt vừa sửa ⇒ toast câu máy chủ (run đã hiện) rồi tải bản mới.
  // Nhận xong mở thẳng trang làm việc để soạn bài.
  const doAcceptBrief = (deal: CreatorDeal) => {
    run(
      `accept:${deal.id}`,
      () => acceptBriefInvite(deal.id, deal.brief?.briefVersion),
      (task) => {
        toast.success('Đã nhận brief, bắt đầu soạn bài nhé.');
        navigate(`${routePaths.brandDeals}/task/${task.dealId}`);
      },
      'Không nhận được lời mời này.',
      goToRatingsTab,
      () => doAcceptBrief(deal)
    ).catch((err) => {
      if (isContentConflict(err)) load();
    });
  };

  const handleAcceptBrief = (deal: CreatorDeal) => {
    if (!deal.brief || !window.confirm(acceptBriefConfirmText(deal.brief))) return;
    doAcceptBrief(deal);
  };

  const handleRequestEdit = (deal: CreatorDeal, note: string): Promise<boolean> =>
    run(
      `edit:${deal.id}`,
      () => requestBrandDealEdit(deal.id, note),
      () => {
        toast.success('Đã gửi đề nghị chỉnh cho người đặt.');
        load();
      },
      'Không gửi được đề nghị chỉnh.'
    )
      .then(() => true)
      .catch(() => false);

  const handleRate = (dealId: string, stars: number, comment: string) =>
    run(
      `rate:${dealId}`,
      () => submitCreatorRating(dealId, stars, comment || undefined),
      () => {
        toast.success('Đã gửi đánh giá.');
        load();
        setPendingRatingsCount((n) => Math.max(0, n - 1));
      },
      'Không gửi được đánh giá.'
    ).catch(() => undefined);

  const invitations = deals.filter((d) => d.status === 'Invited');
  const running = deals.filter((d) => RUNNING_STATUSES.includes(d.status));
  const history = deals.filter((d) => !RUNNING_STATUSES.includes(d.status) && d.status !== 'Invited');

  // Chia theo chiều yêu cầu (sếp góp ý 07/10): việc doanh nghiệp gửi tới mình tách khỏi việc mình gửi đi,
  // rồi mới đến việc đang làm và đánh giá sau hợp tác.
  // Chia theo AI đang phải làm (sếp góp ý 14/10: trang cũ chia theo loại đơn nên không rõ việc nào của mình,
  // việc nào của đối tác): đối tác gửi tới, việc mình đang làm, mình đã gửi đi, tìm việc mới, hồ sơ & cài đặt.
  const navGroups: GroupedNavGroup<Tab>[] = [
    {
      title: 'Đối tác gửi cho bạn',
      items: [
        { key: 'invites', label: 'Lời mời hợp tác', Icon: Mail, badge: invitations.length },
        { key: 'live-orders', label: 'Đơn quảng cáo livestream', Icon: Inbox, badge: pendingLiveOrders },
      ],
    },
    {
      title: 'Việc bạn đang làm',
      items: [
        { key: 'tasks', label: 'Bài đang làm', Icon: ClipboardList, badge: myTurnTasks },
        { key: 'deals', label: 'Bài đã đăng', Icon: Handshake },
        { key: 'live', label: 'Quảng cáo trên livestream', Icon: Radio },
      ],
    },
    {
      title: 'Bạn đã gửi đi',
      items: [
        { key: 'applications', label: 'Đơn ứng tuyển', Icon: Send },
        { key: 'proposals', label: 'Đề xuất', Icon: FileText },
      ],
    },
    {
      title: 'Tìm việc mới',
      items: [
        { key: 'market', label: 'Sàn chiến dịch', Icon: LayoutGrid },
      ],
    },
    {
      title: 'Hồ sơ & cài đặt',
      items: [
        { key: 'settings', label: 'Nhận việc & bảng giá', Icon: Settings },
        { key: 'ratings', label: 'Đánh giá & hồ sơ', Icon: Star, badge: pendingRatingsCount },
      ],
    },
  ];

  // Dải "Việc cần bạn làm": chỉ các việc đang đến lượt creator, bấm là tới đúng mục.
  const todo: { key: Tab; label: string }[] = [
    { key: 'invites' as Tab, label: `${invitations.length} lời mời chờ bạn trả lời`, n: invitations.length },
    { key: 'tasks' as Tab, label: `${myTurnTasks} bài cần bạn làm tiếp`, n: myTurnTasks },
    { key: 'live-orders' as Tab, label: `${pendingLiveOrders} đơn livestream chờ bạn nhận`, n: pendingLiveOrders },
    { key: 'ratings' as Tab, label: `${pendingRatingsCount} đơn chờ bạn đánh giá`, n: pendingRatingsCount },
  ]
    .filter((x) => x.n > 0)
    .map(({ key, label }) => ({ key, label }));

  // Sàn chiến dịch và brief nay nằm ở trang công khai /campaigns; link cũ ?tab=market|briefs chuyển sang đó.
  if (tab === 'market' || tab === 'briefs') return <Navigate to={routePaths.campaigns} replace />;
  if (legacyOrdererLink) return <Navigate to={routePaths.brandOrderer} replace />;

  return (
    <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-6 px-4 py-6 md:px-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-[#EAEFFA] text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
          <Briefcase size={22} />
        </span>
        <div>
          <h1 className="ui-heading text-[22px] font-bold md:text-[26px]">Sàn hợp tác sáng tạo</h1>
          <p className="ui-muted text-[14px]">
            Nhận lời mời hợp tác quảng cáo trực tiếp từ doanh nghiệp, đăng bài và nhận tiền.
          </p>
        </div>
        {/* Người muốn thuê creator: một nút sang trang riêng (sếp góp ý 14/10). */}
        <Link
          to={`${routePaths.brandOrderer}?create=1`}
          className="ml-auto shrink-0 rounded-lg bg-[#0057FF] px-3 py-2 text-[14px] font-semibold text-white hover:brightness-110"
        >
          Tạo chiến dịch thuê creator
        </Link>
      </div>


      {/* Việc đang đến lượt creator — gom từ mọi mục, thay dải nhắc đánh giá riêng lẻ trước đây. */}
      <div className="ui-card flex flex-col gap-2 p-4">
        <div className="ui-heading text-[15px] font-bold">Việc cần bạn làm</div>
        {todo.length === 0 ? (
          <p className="ui-muted text-[13px]">Hiện không có việc nào đang chờ bạn.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {todo.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className="rounded-full bg-amber-100 px-3 py-1.5 text-[13px] font-semibold text-amber-800 hover:brightness-95 dark:bg-amber-900/30 dark:text-amber-200"
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mục lục dạng cây giống trang Cửa hàng: máy tính là cột trái dính khi cuộn, điện thoại là các nút
          xuống dòng — trước đây là một hàng tab cuộn ngang, người dùng không thấy hết các mục. */}
      <div className="flex w-full items-start gap-6">
        <GroupedSectionNavDesktop groups={navGroups} active={tab} onSelect={setTab} />
        <div className="flex min-w-0 flex-1 flex-col gap-6">
      <GroupedSectionNavMobile groups={navGroups} active={tab} onSelect={setTab} />

      {tab === 'applications' && <BrandMarketApplications ref={applicationsRef} />}
      {tab === 'proposals' && <BrandMyProposals />}

      {tab === 'tasks' && <BrandTaskList />}

      {tab === 'settings' && (
        <div className="flex flex-col gap-4">
      {/* Công tắc nhận deal quảng cáo */}
      <div className="ui-card p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="ui-heading text-[15px] font-bold">Nhận deal quảng cáo</div>
            <p className="ui-muted text-[13px]">
              Bật để xuất hiện trong danh bạ creator của doanh nghiệp và nhận lời mời.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!setting?.acceptDeals}
            disabled={loading || !!busy.toggle}
            onClick={handleToggle}
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
              setting?.acceptDeals ? 'bg-[#0057FF]' : 'bg-gray-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`absolute top-0.5 size-6 rounded-full bg-white shadow transition-transform ${
                setting?.acceptDeals ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
        {setting?.acceptDeals && (
          <IndividualOrdersToggle
            setting={setting}
            disabled={loading}
            onChanged={(res) => {
              setSetting(res);
              toast.success(res.acceptIndividualOrders === false ? 'Đã tắt nhận đơn từ cá nhân.' : 'Đã bật nhận đơn từ cá nhân.');
            }}
          />
        )}
        <CreatorTaxCodeField setting={setting} disabled={loading} onSaved={(res) => setSetting(res)} />
        {settingError && (
          <p className="ui-divider mt-3 rounded-lg border bg-amber-50 p-3 text-[13px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            {settingError}
          </p>
        )}
      </div>

          <BrandLiveRateCardForm />
        </div>
      )}

      {tab === 'live' && (
        <div className="flex flex-col gap-4">
          <BrandLiveSlots reloadKey={slotsReloadKey} />
          <BrandLiveCreatorTab
            view="active"
            onChanged={() => setSlotsReloadKey((k) => k + 1)}
            onGoToIncoming={() => setTab('live-orders')}
          />
        </div>
      )}

      {tab === 'live-orders' && (
        <BrandLiveCreatorTab view="incoming" onChanged={() => setSlotsReloadKey((k) => k + 1)} />
      )}

      {tab === 'ratings' && (
        <div className="flex flex-col gap-3">
          {collab?.visible && (
            <a href="/profile?tab=hop-tac" className="text-[13px] text-[#0057FF] underline-offset-2 hover:underline dark:text-blue-300">
              Xem hồ sơ hợp tác của bạn
            </a>
          )}
          <BrandRatingPendingList ref={ratingsRef} viewerRole="Creator" onCountChange={setPendingRatingsCount} />
        </div>
      )}

      {tab === 'invites' && (
        <>
      {!loading && !setting?.acceptDeals && (
        <button
          type="button"
          onClick={() => setTab('settings')}
          className="self-start rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left text-[13px] font-semibold text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300"
        >
          Bạn đang tắt nhận lời mời hợp tác. Bật ở Hồ sơ & cài đặt.
        </button>
      )}

      {/* Hộp lời mời đang chờ */}
      {!loading && invitations.length > 0 && (
        <div className="flex flex-col gap-4">
          <h2 className="ui-heading text-[17px] font-bold">Lời mời đang chờ</h2>
          {invitations.map((deal) =>
            deal.brief ? (
              <BrandBriefInviteCard
                key={deal.id}
                deal={deal}
                netLabel={formatVnd(deal.netVnd)}
                respondByLabel={formatDate(deal.respondBy)}
                busy={!!busy[`accept:${deal.id}`] || !!busy[`decline:${deal.id}`]}
                onAccept={() => handleAcceptBrief(deal)}
                onDecline={() => handleDecline(deal)}
              />
            ) : (
            <BrandInviteCard
              key={deal.id}
              deal={deal}
              authorName={profile?.fullname}
              authorAvatar={profile?.avatar}
              netLabel={formatVnd(deal.netVnd)}
              respondByLabel={formatDate(deal.respondBy)}
              busy={!!busy[`accept:${deal.id}`] || !!busy[`decline:${deal.id}`] || !!busy[`edit:${deal.id}`]}
              onAccept={() => handleAccept(deal)}
              onDecline={() => handleDecline(deal)}
              onRequestEdit={(note) => handleRequestEdit(deal, note)}
            />
            )
          )}
        </div>
      )}

      {!loading && invitations.length === 0 && (
        <p className="ui-muted text-[14px]">Chưa có lời mời nào đang chờ bạn trả lời.</p>
      )}
      {running.length > 0 && (
        <button
          type="button"
          onClick={() => setTab('deals')}
          className="self-start text-[14px] font-semibold text-[#0057FF]"
        >
          Bạn có {running.length} deal đang chạy, xem tiến độ
        </button>
      )}
        </>
      )}

      {tab === 'deals' && (
        <>
      {/* Deal đang chạy */}
      <div className="flex flex-col gap-3">
        <h2 className="ui-heading text-[17px] font-bold">Deal đang chạy</h2>
        {!loading && running.length === 0 && (
          <p className="ui-muted text-[14px]">Chưa có deal nào đang chạy.</p>
        )}
        {running.length > 0 && (
          <div className="ui-card divide-y ui-divider">
            {running.map((deal) => (
              <DealRow key={deal.id} deal={deal} onRate={handleRate} ratingBusy={!!busy[`rate:${deal.id}`]} />
            ))}
          </div>
        )}
      </div>

      {/* Lịch sử */}
      <div className="flex flex-col gap-3">
        <h2 className="ui-heading text-[17px] font-bold">Lịch sử</h2>
        {!loading && history.length === 0 && <p className="ui-muted text-[14px]">Chưa có lịch sử.</p>}
        {history.length > 0 && (
          <div className="ui-card divide-y ui-divider">
            {history.map((deal) => (
              <DealRow key={deal.id} deal={deal} onRate={handleRate} ratingBusy={!!busy[`rate:${deal.id}`]} />
            ))}
          </div>
        )}
      </div>
        </>
      )}
        </div>
      </div>
      {taxConsent.dialog}
    </div>
  );
};

// Khai ngoài component trang để không bị dựng lại (mất trạng thái) mỗi lần trang render.
const DealRow: React.FC<{
  deal: CreatorDeal;
  onRate: (dealId: string, stars: number, comment: string) => void;
  ratingBusy: boolean;
}> = ({ deal, onRate, ratingBusy }) => (
  <div className="flex flex-col gap-3 p-4 text-[14px]">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-col gap-0.5">
        <div className="ui-text font-semibold">{deal.campaign.title}</div>
        <div className="ui-muted flex flex-wrap items-center gap-x-3">
          <span>{dealStatusLabel(deal.status)}</span>
          {deal.endsAt && <span>Kết thúc: {formatDate(deal.endsAt)}</span>}
          <span className="inline-flex items-center gap-1">
            <MousePointerClick size={14} /> {deal.validClicks} click
          </span>
          {/* M-7: "Nhận {net}" trên MỌI dòng từng gây hiểu lầm — Declined/Expired/Forfeited/PublishFailed
              không hề nhận tiền. Chỉ Completed mới thật sự "Đã nhận"; còn lại chỉ là giá deal tham khảo. */}
          <span>{deal.status === 'Completed' ? `Đã nhận ${formatVnd(deal.netVnd)}` : `Giá deal ${formatVnd(deal.netVnd)}`}</span>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <BrandThreadLink dealId={deal.id} threadId={deal.threadId} />
        {deal.postId && (
          <Link to={`/share/post/${deal.postId}`} className="font-semibold text-[#0057FF]">
            Xem bài
          </Link>
        )}
      </div>
    </div>
    {/* Đo chuyển đổi (spec 2026-10-05-brand-conversion §3) — lượt bấm, chuyển đổi, tỉ lệ. */}
    <div className="ui-muted text-[13px]">
      {deal.stats.clicks} lượt bấm · {deal.stats.conversions} chuyển đổi · tỉ lệ {formatRate(deal.stats.conversions, deal.stats.clicks)}
    </div>
    {/* M5: ô chấm điểm doanh nghiệp + đánh giá đã công bố — ẩn hẳn nếu deal chưa mở chấm. */}
    <BrandRatingBox
      ratingDueAt={deal.ratingDueAt}
      myRating={deal.myRating}
      theirRating={deal.theirRating}
      counterpartLabel="Doanh nghiệp đánh giá bạn"
      busy={ratingBusy}
      onSubmit={(stars, comment) => onRate(deal.id, stars, comment)}
    />
  </div>
);

export default BrandDealsPage;
